import type { ExternalObjectConf } from '@bageltechnologies/bagel-common';
import { axiosInstance } from './axios-instance';
import type { PaginatedResult, PaginationDto } from '../types/common';
import type { IntegrationObjectsMetaPage } from '../types/integration-meta';
import type { AdminIntegrationByKey } from '../types/admin-integration-by-key';

export interface IntegrationObjectsFilters {
  accountId: string;
  /** Integration key (e.g. `salesforce`). Omit to list across all integrations for the account. */
  origin?: string;
  filterBy?: string;
}

export interface UpsertIntegrationObjectParams {
  accountId: string;
  origin: string;
}

/**
 * Admin per-object config, backed by the shipped `/admin/integration-objects` endpoints
 * (branch `feat/integrations-v2-config-objects`). Returns/accepts `ExternalObjectConf` docs
 * (the `connected.externalObjects` side). There is NO `available` spec here — see the gated
 * features in plans/copy-integrations.md.
 */
export class IntegrationObjectService {
  static async getObjects(
    filters: IntegrationObjectsFilters,
    pagination?: PaginationDto,
  ): Promise<PaginatedResult<ExternalObjectConf>> {
    const res = await axiosInstance.get<PaginatedResult<ExternalObjectConf>>(
      '/admin/integration-objects',
      {
        // pageSize 1000 = fetch all configured objects for the integration in one shot
        // (an account rarely configures that many); sortBy=updatedAt per BE (no createdAt).
        params: { pageSize: 1000, sortBy: 'updatedAt', sortDir: 'desc', ...pagination, ...filters },
      },
    );
    return res.data;
  }

  static async upsertObject(
    params: UpsertIntegrationObjectParams,
    body: Partial<ExternalObjectConf>,
  ): Promise<ExternalObjectConf> {
    const res = await axiosInstance.put<ExternalObjectConf>('/admin/integration-objects', body, {
      params,
    });
    return res.data;
  }

  /**
   * One page of the discovered source-object catalogue
   * (`GET /admin/integration-objects-meta`, backed by `integration_objects_meta`).
   *
   * `fieldsMeta` is stripped from a list page - configured objects come back already enriched
   * from `GET /admin/integration-objects`, and a single object's fields can be requested with
   * `originObjectKey` + `includeFieldsMeta`. An empty page means metadata has not been synced
   * yet for that account + integration; there is no 404 for it any more.
   */
  static async getIntegrationObjectsMeta(
    accountId: string,
    origin: string,
    params: { page?: number; pageSize?: number; filterBy?: string } = {},
  ): Promise<IntegrationObjectsMetaPage> {
    const res = await axiosInstance.get<IntegrationObjectsMetaPage>(
      '/admin/integration-objects-meta',
      {
        params: { accountId, origin, sortBy: 'name', sortDir: 'asc', ...params },
      },
    );
    return res.data;
  }

  /**
   * Cross-tenant drop-in twin of the customer `GET /integrations/v2/:key` — returns the full
   * `{ available, connected, selectedScopes, bagelActions }` view. `available.externalObjects[]`
   * carry the default `.fields`; `connected.externalObjects[]` are `fieldsMeta`-enriched. Used to
   * source the field-mapping drawer's "Default Field Mapping" section. 404 when the integration key
   * is unknown in the catalog.
   */
  static async getIntegrationByKey(
    accountId: string,
    integrationKey: string,
  ): Promise<AdminIntegrationByKey> {
    const res = await axiosInstance.get<AdminIntegrationByKey>('/admin/integration-by-key', {
      params: { accountId, integrationKey },
    });
    return res.data;
  }
}
